import { SecondaryButton } from '@workspace/ui/components/dsm/secondary-button'
import { cn } from '@workspace/ui/lib/utils'
import { Mail, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import {
  useAdminInvestorDealFlowMatchesQuery,
  useAdminRefreshInvestorDealFlowMatchesMutation,
  useAdminSendInvestorDealFlowDigestMutation,
} from '../../../hooks/api'

type RefreshStatus = 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED'

type VisibilityReason = 'visible' | 'filtered_geographies' | 'filtered_areas' | 'passed' | 'stealth'

const VISIBILITY_STYLES: Record<VisibilityReason, { label: string; className: string }> = {
  visible: {
    label: 'Visible',
    className: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  },
  filtered_geographies: {
    label: 'Filtered: geo',
    className: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  },
  filtered_areas: {
    label: 'Filtered: sectors',
    className: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  },
  passed: {
    label: 'Passed',
    className: 'border-zinc-500/40 bg-zinc-500/10 text-zinc-300',
  },
  stealth: {
    label: 'Stealth',
    className: 'border-zinc-500/40 bg-zinc-500/10 text-zinc-300',
  },
}

function VisibilityBadge({ reason }: { reason: VisibilityReason }) {
  const style = VISIBILITY_STYLES[reason] ?? VISIBILITY_STYLES.visible
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium uppercase',
        style.className
      )}
    >
      {style.label}
    </span>
  )
}

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '—'
  return new Date(date).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function StatusBadge({ status }: { status: RefreshStatus }) {
  const styles: Record<RefreshStatus, string> = {
    PENDING: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
    PROCESSING: 'border-blue-500/40 bg-blue-500/10 text-blue-300',
    COMPLETED: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    FAILED: 'border-red-500/40 bg-red-500/10 text-red-300',
  }
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium uppercase',
        styles[status]
      )}
    >
      {status}
    </span>
  )
}

export function InvestorDealFlowStartupsTab({ investorId }: { investorId: string }) {
  const query = useAdminInvestorDealFlowMatchesQuery(investorId)
  const refresh = useAdminRefreshInvestorDealFlowMatchesMutation(investorId)
  const sendDigest = useAdminSendInvestorDealFlowDigestMutation()
  const [digestResult, setDigestResult] = useState<string | null>(null)

  const allMatches = (query.data?.data?.matches ?? []) as Array<
    NonNullable<typeof query.data>['data']['matches'][number] & {
      visibilityReason?: VisibilityReason
    }
  >
  const [onlyVisible, setOnlyVisible] = useState(false)
  const matches = onlyVisible
    ? allMatches.filter((m) => m.visibilityReason === 'visible')
    : allMatches
  const visibleCount = allMatches.filter((m) => m.visibilityReason === 'visible').length
  const latest = query.data?.data?.latestRefreshRequest ?? null
  const isInFlight = latest?.status === 'PENDING' || latest?.status === 'PROCESSING'

  const handleRefresh = () => {
    refresh.mutate({ path: { id: investorId } })
  }

  const handleSendDigest = () => {
    setDigestResult(null)
    sendDigest.mutate(
      { path: { id: investorId } },
      {
        onSuccess: (resp) => {
          const data = resp?.data
          setDigestResult(
            data
              ? `Sent ${data.emailsSent} email${data.emailsSent === 1 ? '' : 's'} with ${data.matchesIncluded} match${data.matchesIncluded === 1 ? '' : 'es'}.`
              : 'Digest send completed.'
          )
        },
        onError: (err) => {
          setDigestResult(`Failed: ${(err as Error).message}`)
        },
      }
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-card/40 px-4 py-3">
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Latest refresh:</span>
            {latest ? (
              <>
                <StatusBadge status={latest.status as RefreshStatus} />
                <span>{latest.triggerReason}</span>
                <span>•</span>
                <span>{formatDate(latest.completedAt ?? latest.createdAt)}</span>
                {latest.persisted !== null && <span>• {latest.persisted} persisted</span>}
              </>
            ) : (
              <span>No refresh runs yet.</span>
            )}
          </div>
          {latest?.error && <p className="text-xs text-red-400">Error: {latest.error}</p>}
        </div>

        <SecondaryButton
          size="sm"
          icon={<RefreshCw />}
          onClick={handleRefresh}
          loading={refresh.isPending || isInFlight}
        >
          Regenerate
        </SecondaryButton>

        <SecondaryButton
          size="sm"
          icon={<Mail />}
          onClick={handleSendDigest}
          loading={sendDigest.isPending}
          disabled={matches.length === 0}
        >
          Send test email
        </SecondaryButton>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs">
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={onlyVisible}
            onChange={(e) => setOnlyVisible(e.target.checked)}
            className="size-3.5"
          />
          <span>Only visible to investor</span>
        </label>
        <span className="text-muted-foreground">
          Showing <strong className="text-foreground">{matches.length}</strong> of{' '}
          <strong className="text-foreground">{allMatches.length}</strong> raw matches (
          <strong className="text-emerald-400">{visibleCount}</strong> pass all filters)
        </span>
      </div>

      {digestResult && (
        <div className="rounded-md border border-border bg-card/40 px-4 py-2 text-xs text-muted-foreground">
          {digestResult}
        </div>
      )}

      {query.isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
      ) : matches.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border/60 bg-card/30 px-6 py-12 text-center text-sm text-muted-foreground">
          No deal-flow matches yet. Click Regenerate to fetch from Recombee.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-xs text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Startup</th>
                <th className="px-4 py-2 text-left font-medium">Visibility</th>
                <th className="px-4 py-2 text-right font-medium">Score</th>
                <th className="px-4 py-2 text-left font-medium">First seen</th>
                <th className="px-4 py-2 text-left font-medium">Last seen</th>
                <th className="px-4 py-2 text-left font-medium">Notified</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {matches.map((m) => (
                <tr key={m.startupId} className="hover:bg-muted/20">
                  <td className="px-4 py-2">
                    <div className="font-medium text-foreground">{m.startupName}</div>
                    <div className="text-[11px] text-muted-foreground">{m.startupId}</div>
                  </td>
                  <td className="px-4 py-2">
                    <VisibilityBadge reason={m.visibilityReason ?? 'visible'} />
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">{m.recombeeScore}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {formatDate(m.firstSeenAt)}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    {formatDate(m.lastSeenAt)}
                  </td>
                  <td className="px-4 py-2 text-xs">
                    {m.notifiedAt ? (
                      <span className="text-emerald-400">{formatDate(m.notifiedAt)}</span>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
